import type { LoSiteBlock } from '@/lib/lyrraone-site-blocks';
import type { LoSiteElementStyle } from '@/lib/lyrraone-site-elements';
import { elementStyleClass, elementStyleToCss } from '@/lib/lyrraone-site-elements';
import { ThemeEditable } from '@/components/lyrraone/site-themes/LoSiteThemeEditable';
import { cn } from '@/lib/utils';

interface LoSiteThemedTestimonialsProps {
  block: LoSiteBlock;
  blockKey: string;
  editMode?: boolean;
  selectedElementKey?: string | null;
  onSelectElement?: (key: string) => void;
  onInlineTextChange?: (key: string, value: string) => void;
  styleOf: (key: string) => LoSiteElementStyle | undefined;
}

export function LoSiteThemedTestimonials({
  block,
  blockKey,
  editMode,
  selectedElementKey,
  onSelectElement,
  onInlineTextChange,
  styleOf,
}: LoSiteThemedTestimonialsProps) {
  const items = block.testimonials ?? [];

  return (
    <section key={blockKey} className="lo-section alt">
      <div className="lo-wrap">
        <div className="lo-section-head">
          {block.title && (
            <ThemeEditable
              elementKey="testimonials.title"
              editMode={editMode}
              selectedElementKey={selectedElementKey}
              onSelectElement={onSelectElement}
              onInlineTextChange={onInlineTextChange}
              className={cn('lo-h2', elementStyleClass(styleOf('testimonials.title')))}
              style={elementStyleToCss(styleOf('testimonials.title'))}
              as="h2"
              editableText
            >
              {block.title}
            </ThemeEditable>
          )}
          {block.subtitle && (
            <ThemeEditable
              elementKey="testimonials.subtitle"
              editMode={editMode}
              selectedElementKey={selectedElementKey}
              onSelectElement={onSelectElement}
              onInlineTextChange={onInlineTextChange}
              className={cn('lo-lead', elementStyleClass(styleOf('testimonials.subtitle')))}
              style={elementStyleToCss(styleOf('testimonials.subtitle'))}
              as="p"
              editableText
            >
              {block.subtitle}
            </ThemeEditable>
          )}
        </div>
        <div className="lo-testimonials">
          {items.map((item, i) => {
            const quoteKey = `testimonials.item.${i}.quote`;
            const nameKey = `testimonials.item.${i}.name`;
            const roleKey = `testimonials.item.${i}.role`;
            return (
              <figure key={i} className="lo-testimonial">
                <ThemeEditable
                  elementKey={quoteKey}
                  editMode={editMode}
                  selectedElementKey={selectedElementKey}
                  onSelectElement={onSelectElement}
                  onInlineTextChange={onInlineTextChange}
                  className={cn(elementStyleClass(styleOf(quoteKey)))}
                  style={elementStyleToCss(styleOf(quoteKey))}
                  as="blockquote"
                  editableText
                >
                  {`« ${item.quote} »`}
                </ThemeEditable>
                <cite>
                  <ThemeEditable
                    elementKey={nameKey}
                    editMode={editMode}
                    selectedElementKey={selectedElementKey}
                    onSelectElement={onSelectElement}
                    onInlineTextChange={onInlineTextChange}
                    as="span"
                    editableText
                  >
                    {item.name}
                  </ThemeEditable>
                  {item.role && (
                    <ThemeEditable
                      elementKey={roleKey}
                      editMode={editMode}
                      selectedElementKey={selectedElementKey}
                      onSelectElement={onSelectElement}
                      onInlineTextChange={onInlineTextChange}
                      as="span"
                      editableText
                    >
                      {item.role}
                    </ThemeEditable>
                  )}
                </cite>
              </figure>
            );
          })}
        </div>
      </div>
    </section>
  );
}
